import { reactive } from 'vue';

export interface NxContextMenuState<T> {
  open: boolean;
  x: number;
  y: number;
  anchor: HTMLElement | null;
  /** What the menu is about (the right-clicked row, message, file …). */
  target: T | null;
  /** Opens at the pointer of a `contextmenu`/`click` event (and suppresses the browser menu). */
  openAt: (event: MouseEvent, target?: T) => void;
  /** Opens beside an element (a „…“ button); pass `event.currentTarget`. */
  openFor: (anchor: HTMLElement, target?: T) => void;
  close: () => void;
}

/**
 * State for one NxContextMenu, so a caller wires it in two lines:
 *
 * ```vue
 * const menu = useNxContextMenu<Row>();
 * <tr @contextmenu="menu.openAt($event, row)">
 * <NxContextMenu v-model:open="menu.open" :x="menu.x" :y="menu.y" :anchor="menu.anchor">
 * ```
 */
export function useNxContextMenu<T = unknown>(): NxContextMenuState<T> {
  const state = reactive({
    open: false,
    x: 0,
    y: 0,
    anchor: null as HTMLElement | null,
    target: null as T | null,
    openAt(event: MouseEvent, target?: T) {
      event.preventDefault();
      state.anchor = null;
      state.x = event.clientX;
      state.y = event.clientY;
      state.target = (target ?? null) as never;
      state.open = true;
    },
    openFor(anchor: HTMLElement, target?: T) {
      state.anchor = anchor;
      state.target = (target ?? null) as never;
      state.open = true;
    },
    close() {
      state.open = false;
    },
  });
  return state as NxContextMenuState<T>;
}
